import React, { useState, useMemo, Fragment, useCallback } from 'react';
import { isString } from 'lodash';
import {
  getSingleDevice,
  updateSingleDevicePageSelectedDeviceHeatmapRowIdx,
  updateSingleDevicePageSelectedRasterRecord,
  updateSingleDevicePageSelectedRasterId,
  updateSingleDevicePageSelectedBeamRankIdx,
  updateSingleDevicePageHeatmapLibraryTableSetSelectAll,
  updateSingleDevicePageSelectedRastersDictionary,
  doGetRasterRecord,
  doGetRasterRecordsForDevice,
  setStatusNotification,
  updateStatusNotification,
  deleteStatusNotification,
  doLockUnlock,
  doSetWifi,
  doBlinkLED,
  updatePivotOverviewPageSingleSelectedDevice,
  updateEchoOverviewPageSelectedDevice
} from 'redux/actions';
import {
  useDeviceReboot,
  useRebootDeviceBundle,
  RebootGA1DeviceDialog,
  RebootGA1DeviceBundleDialog,
  RebootGA2DeviceDialog
} from 'components/features/RebootDevice';
import { GainSettingsDialog } from 'components/features/GainSettings';
import HeatmapViewAndExportModal from 'components/features/HeatmapViewAndExportModal/HeatmapViewAndExportModal';
import HeatmapRetrieveModal from 'components/features/HeatmapRetrieveModal/HeatmapRetrieveModal';
import HeatmapDiagRasterModal from 'components/features/HeatmapDiagRaster/HeatmapDiagRasterModal/HeatmapDiagRasterModal';
import { IconAndText } from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import Dropdown from 'components/shared/Dropdown/Dropdown';
import HeatmapImageCollectingScreen from 'components/features/HeatmapImageCollectingScreen/HeatmapImageCollectingScreen';
import { useDispatch, useSelector } from 'react-redux';
import './MaintenancePanelBar.css';
import {
  DEVICE_NOT_ONLINE_ERROR_MESSAGE,
  PERMISSION_ERROR_MESSAGE,
  Status,
  BLINK_DEVICE_LED_MIN_API_VERSION
} from 'common/utils/constants';
import CreateSiteModal from 'components/features/Commissioning/CreateSiteModal';
import MaintenancePanelTilt from './MaintenancePanelTilt/MaintenancePanelTilt';
import MaintenancePanelDropdownTilt from './MaintenancePanelDropdownTilt/MaintenancePanelDropdownTilt';
import MaintenanceLogsPanelTilt from './MaintenanceLogsPanelTilt';
import classNames from 'classnames';
import WiFiAccessPointModal from '../WiFiAccessPointModal/WiFiAccessPointModal';
import WiFiTimersModal from '../WiFiTimersModal/WiFiTimersModal';
import LockUnlockModal from '../LockUnlockModal/LockUnlockModal';
import { isDeviceOnline, isBundleOnline, isDeviceLocked, isDeviceWiFiEnabled } from 'common/utils/deviceTwinUtils';
import PivotsLockModal from './PivotsLockModal/PivotsLockModal';
import ProvisionModal from '../ProvisionModal/ProvisionModal';
import CommissionGA2Modal, { canCommissionGA2, validateCommissionGA2Device } from '../CommissionGA2Modal';
import GA2SwVersionModal from '../GA2SwVersionModal/GA2SwVersionModal';
import MaintenancePanelHeader from './MaintenancePanelHeader/MaintenancePanelHeader';
import {
  hasCommissionPermission,
  hasCmGetRasterHeatmapPermission,
  hasCmCreateRasterHeatmapPermission,
  hasCmRebootPermission,
  hasCmGainSettingsPermission,
  hasGa2ProvisionPermission,
  rebootSuccessMessage
} from './utils';
import { GA2ServiceMenubar } from './components';
import { useDeviceGainSettings } from './hooks';
import GA2RFValuesModal from '../GA2RFValuesModal/GA2RFValuesModal';
import { doGetArfcnValues } from 'redux/Commissioning';

// added this to check api version.
const meetMinApiVersion = (apiVersion, minVersion) => {
  if (!apiVersion) return false;
  const version = parseInt(apiVersion.substring(1));
  return !isNaN(version) && version >= minVersion;
};

export const MaintenancePanelBar = (props) => {
  const dispatch = useDispatch();
  const permissions = useSelector((store) => store.data?.rolePermissions?.permissions);
  const deviceWiFiStatusName = `SetWifi-${props.targetDevice?.deviceId}`;
  const deviceWiFiStatus = useSelector((store) => store.statuses?.[deviceWiFiStatusName]);
  const isWiFiActionPending = deviceWiFiStatus === Status.PENDING;
  const deviceLockUnlockStatusName = `LockUnlock-${props.targetDevice?.deviceId}`;
  const deviceLockUnlockStatus = useSelector((store) => store.statuses?.[deviceLockUnlockStatusName]);
  const isLockUnlockActionPending = deviceLockUnlockStatus === Status.PENDING;
  const [isHeatmapViewAndExportModalOpen, setIsHeatmapViewAndExportModalOpen] = useState(false);
  const [isHeatmapRetrieveModalOpen, setIsHeatmapRetrieveModalOpen] = useState(false);
  const [shouldFetchHeatmapRecords, setShouldFetchHeatmapRecords] = useState(true);
  const [isCollectingFiles, setIsCollectingFiles] = useState(false);
  const [isWifiTimersModalOpen, setIsWifiTimersModalOpen] = useState(false);
  const [isWifiAccessPointModalOpen, setIsWifiAccessPointModalOpen] = useState(false);
  const [wifiEnabledTimerValue, setWifiEnabledTimerValue] = useState(1);
  const [wifiDisabledTimerValue, setWifiDisabledTimerValue] = useState(1);
  const [accessPointNameValue, setAccessPointNameValue] = useState(null);
  const [accessPointPasswordValue, setAccessPointPasswordValue] = useState(null);
  const [accessModalStatus, setAccessModalStatus] = useState(Status.NONE);
  const [timersModalStatus, setTimersModalStatus] = useState(Status.NONE);
  const [accessModalErrorMessage, setAccessModalErrorMessage] = useState(null);
  const [, setTimersModalErrorMessage] = useState(null);
  const [isHeatmapDiagRasterModalOpen, setIsHeatmapDiagRasterModalOpen] = useState(false);
  const [isProvisionModalOpen, setIsProvisionModalOpen] = useState(false);
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [isPivotLockModalOpen, setIsPivotLockModalOpen] = useState(false);
  const [isReCommissionModalOpen, setIsReCommissionModalOpen] = useState(false);
  const [isSwVersionModalOpen, setIsSwVersionModalOpen] = useState(false);
  const [isRFValuesModalOpen, setIsRFValuesModalOpen] = useState(false);
  const [isLockUnlockModalOpen, setIsLockUnlockModalOpen] = useState(false);
  const targetDeviceAlarms = useSelector((state) => state.data?.alarms?.deviceAlarms?.[props.targetDevice?.deviceId]);
  const criticalTargetDeviceAlarms = useMemo(
    () => (targetDeviceAlarms ?? []).filter((alarm) => alarm.severity.toLowerCase() === 'critical'),
    [targetDeviceAlarms]
  );

  const canGetRaster = hasCmGetRasterHeatmapPermission(permissions);
  const canCreateRaster = hasCmCreateRasterHeatmapPermission(permissions);
  const hasNoRasterPermissions = !canCreateRaster && !canGetRaster;
  const canCommission = hasCommissionPermission(permissions);
  const canProvisionGa2 = hasGa2ProvisionPermission(permissions);
  const canReboot = hasCmRebootPermission(permissions);
  const canSetDeviceGains = hasCmGainSettingsPermission(permissions);
  const isOnline = props.bundle ? isBundleOnline(props.bundle) : isDeviceOnline(props.targetDevice);

  const deviceProductType = props.targetDevice?.properties?.reported?.productType;
  const isHBFServiceDevice = deviceProductType === 'HBF_SERVICE' && props.targetDeviceType === 'service';

  const handleUpdateSingleDevice = useCallback(
    (deviceId) => {
      dispatch(
        getSingleDevice(deviceId, (result) => {
          props.targetDeviceType === 'echo'
            ? dispatch(updateEchoOverviewPageSelectedDevice(result))
            : dispatch(updatePivotOverviewPageSingleSelectedDevice(result));
        })
      );
    },
    [dispatch, props.targetDeviceType]
  );
  const handleRebootDeviceMutate = useCallback(() => {
    dispatch(setStatusNotification({ id: 'reboot', text: 'Reboot Requested', status: Status.PENDING }));
  }, [dispatch]);
  const handleRebootDeviceSuccess = useCallback(
    (_, variables) => {
      dispatch(
        updateStatusNotification({
          id: 'reboot',
          text: rebootSuccessMessage,
          status: Status.SUCCESS,
          icon: IconLoader.icon_reboot_success
        })
      );
      handleUpdateSingleDevice(variables.deviceId);
      setTimeout(() => {
        deleteStatusNotification({ id: 'reboot' });
      }, 3000);
    },
    [dispatch, handleUpdateSingleDevice]
  );
  const handleRebootDeviceError = useCallback(
    (error) => {
      let errorMessage = error.message ?? 'Error';

      if (error?.response?.status === '403') {
        errorMessage = 'Device is Offline';
      }

      dispatch(updateStatusNotification({ id: 'reboot', text: errorMessage, status: Status.FAILURE }));
    },
    [dispatch]
  );
  const rebootDevice = useDeviceReboot(props.targetDevice, {
    onMutate: handleRebootDeviceMutate,
    onSuccess: handleRebootDeviceSuccess,
    onError: handleRebootDeviceError
  });
  const handleRebootDeviceBundleMutate = useCallback(
    (variables) => {
      dispatch(
        setStatusNotification({
          id: 'multiReboot',
          mode: 'multi',
          actions: variables.members.reduce((acc, member) => {
            acc[member.deviceId] = {
              id: 'reboot',
              type: member.deviceId === props.targetDevice?.deviceId ? 'donor' : 'service',
              text: 'Reboot Requested',
              status: Status.PENDING
            };

            return acc;
          }, {})
        })
      );
    },
    [dispatch, props.targetDevice?.deviceId]
  );
  const handleRebootDeviceBundleSuccess = useCallback(
    (_, variables) => {
      dispatch(
        updateStatusNotification({
          id: 'multiReboot',
          actions: {
            [variables.deviceId]: {
              text: rebootSuccessMessage,
              icon: IconLoader.led_status_green,
              status: Status.SUCCESS
            }
          }
        })
      );
      handleUpdateSingleDevice(variables.deviceId);
    },
    [dispatch, handleUpdateSingleDevice]
  );
  const handleRebootDeviceBundleError = useCallback(
    (_, variables) => {
      dispatch(
        updateStatusNotification({
          id: 'multiReboot',
          actions: {
            [variables.deviceId]: {
              text: 'Failed to reboot.',
              icon: IconLoader.warning_circle,
              status: Status.FAILURE
            }
          }
        })
      );
    },
    [dispatch]
  );
  const rebootDeviceBundle = useRebootDeviceBundle(
    props.bundle ?? { members: [props.targetDevice, ...(props.serviceDevices ?? [])] },
    {
      onMutate: handleRebootDeviceBundleMutate,
      onSuccess: handleRebootDeviceBundleSuccess,
      onError: handleRebootDeviceBundleError
    }
  );
  const gainSettings = useDeviceGainSettings(props.targetDevice?.deviceId);
  const launchWiFiTimersModal = () => {
    !isWifiTimersModalOpen && setIsWifiTimersModalOpen(true);

    const hasReportedWifi = props.targetDevice.properties?.reported?.wifi;
    const hasWifiEnableTimerValue = hasReportedWifi && props.targetDevice.properties?.reported?.wifi?.enableTimer;
    const hasWifiDisableTimerValue = hasReportedWifi && props.targetDevice.properties?.reported?.wifi?.disableTimer;

    hasWifiEnableTimerValue ? setWifiEnabledTimerValue(hasWifiEnableTimerValue / 60) : setWifiEnabledTimerValue(0);
    hasWifiDisableTimerValue ? setWifiDisabledTimerValue(hasWifiDisableTimerValue / 60) : setWifiDisabledTimerValue(0);
  };

  const launchWiFiAccessPointModal = () => {
    const deviceWifiSSID = props.targetDevice?.properties?.reported?.wifi?.ssid || '';
    const deviceWifiPSK = props.targetDevice?.properties?.reported?.wifi?.psk || '';
    setAccessPointNameValue(deviceWifiSSID);
    setAccessPointPasswordValue(deviceWifiPSK);

    !isWifiAccessPointModalOpen && setIsWifiAccessPointModalOpen(true);
  };

  const applyWifiAccessPoint = () => {
    if (accessModalStatus !== Status.NONE) return;

    setAccessModalStatus(Status.PENDING);

    dispatch(
      doSetWifi(
        props.targetDevice.deviceId,
        null,
        accessPointNameValue,
        accessPointPasswordValue,
        null,
        null,
        (result) => {
          if (result && result.customStatus && result.customStatus.status && result.customStatus.status === 'error') {
            setAccessModalStatus(Status.FAILURE);
            setAccessModalErrorMessage(result.customStatus.message);
          } else {
            setAccessModalStatus(Status.SUCCESS);
            handleUpdateSingleDevice(props.targetDevice?.deviceId);
          }
        },
        (error) => {
          setAccessModalStatus(Status.FAILURE);
          let errorText = error && error.message ? error.message : JSON.stringify(error);

          setAccessModalErrorMessage(errorText ? errorText : 'Unknown error');
        }
      )
    );
  };

  const applyWifiTimers = () => {
    setTimersModalStatus(Status.PENDING);

    dispatch(
      doSetWifi(
        props.targetDevice.deviceId,
        null,
        null,
        null,
        wifiEnabledTimerValue * 60,
        wifiDisabledTimerValue * 60,
        (result) => {
          if (result && result.customStatus && result.customStatus.status && result.customStatus.status === 'error') {
            setTimersModalStatus(Status.FAILURE);
            setTimersModalErrorMessage(result.customStatus.message);
          } else {
            setTimersModalStatus(Status.SUCCESS);
            handleUpdateSingleDevice(props.targetDevice?.deviceId);
          }
        },
        (error) => {
          setTimersModalStatus(Status.FAILURE);
          let errorText = error && error.message ? error.message : JSON.stringify(error);
          setTimersModalErrorMessage(errorText ? errorText : 'Unknown error');
        }
      )
    );
  };

  const closeHeatmapViewAndExportModal = () => {
    setIsHeatmapViewAndExportModalOpen(false);
    setIsHeatmapRetrieveModalOpen(false);
    setShouldFetchHeatmapRecords(true);
    dispatch(updateSingleDevicePageSelectedRasterRecord(null));
    dispatch(updateSingleDevicePageSelectedRasterId(null));
    dispatch(updateSingleDevicePageSelectedDeviceHeatmapRowIdx(0));
    dispatch(updateSingleDevicePageSelectedBeamRankIdx(1));
    dispatch(updateSingleDevicePageHeatmapLibraryTableSetSelectAll(false));
    dispatch(updateSingleDevicePageSelectedRastersDictionary(null));
  };

  const closeHeatmapRetrieveModal = () => {
    setIsHeatmapRetrieveModalOpen(false);
  };

  const closeHeatmapDiagRasterModal = () => {
    setIsHeatmapDiagRasterModalOpen(false);
  };

  const closeReCommissionModal = () => {
    setIsReCommissionModalOpen(false);
  };

  const closeSwVersionModal = () => {
    setIsSwVersionModalOpen(false);
  };

  const closeRFValuesModal = () => {
    setIsRFValuesModalOpen(false);
  };

  const fetchRasterRecordsForDevice = () => {
    const cb_success = (res) => {
      if (!res || res.length === 0) {
        return; // Exit early if there's no data
      }

      // Find the first valid record
      const validRecord = res.find((record) => record.rasterStatus !== 'fail');

      if (!validRecord) {
        console.log('All records have rasterStatus as fail');
        return; // Exit if no valid record is found
      }
      const rasterId = validRecord.rasterId;

      // Callback function to dispatch actions
      const successCallback = (record) => {
        dispatch(updateSingleDevicePageSelectedRasterRecord(record));
        dispatch(updateSingleDevicePageSelectedRasterId(record.rasterId));
      };

      // Dispatch the action to fetch the raster record
      dispatch(doGetRasterRecord(props.targetDevice?.deviceId, rasterId, successCallback));
    };
    isHeatmapViewAndExportModalOpen && dispatch(doGetRasterRecordsForDevice(props.targetDevice?.deviceId, cb_success));
  };

  const handlePivotLock = (pivotLockMode) => {
    setIsPivotLockModalOpen(false);
    const donorId = props.targetDevice.deviceId;
    const serviceIds = props.serviceDevices?.map((x) => x.deviceId) || [];
    const deviceIdList = [donorId, ...serviceIds];
    const actions = {};
    deviceIdList.forEach((deviceId, index) => {
      if (deviceId === undefined || deviceId === null) return;
      actions[deviceId] = {
        id: 'pivotLock' + pivotLockMode,
        type: index === 0 ? 'donor' : 'service',
        text: pivotLockMode + 'ing...',
        icon: pivotLockMode === 'unlock' ? IconLoader.blue_spinner : IconLoader.red_spinner,
        status: Status.PENDING
      };
    });

    dispatch(
      setStatusNotification({
        id: 'pivotLock',
        mode: 'multi',
        actions
      })
    );

    deviceIdList.forEach((deviceId) => {
      dispatch(
        doLockUnlock(
          deviceId,
          pivotLockMode,
          () => {
            dispatch(
              updateStatusNotification({
                id: 'pivotLock',
                actions: {
                  [deviceId]: {
                    text: pivotLockMode + 'ed',
                    icon: pivotLockMode === 'lock' ? IconLoader.icon_locked_status : IconLoader.icon_unlocked_status,
                    status: Status.SUCCESS
                  }
                }
              })
            );
            handleUpdateSingleDevice(deviceId);
          },
          (error) => {
            dispatch(
              updateStatusNotification({
                id: 'pivotLock',
                actions: {
                  [deviceId]: {
                    text: 'Failed to ' + pivotLockMode,
                    icon: IconLoader.warning_circle,
                    errorMsg: error,
                    status: Status.FAILURE
                  }
                }
              })
            );
          }
        )
      );
    });
  };

  const handleLockUnlockAction = (device) => {
    const isLocked = isDeviceLocked(device);
    dispatch(
      setStatusNotification({
        id: isLocked ? 'unlock' : 'lock',
        text: isLocked ? 'Unlocking the device...' : 'Locking the device...',
        status: Status.PENDING,
        hideCloseButton: true
      })
    );

    dispatch(
      doLockUnlock(
        device?.deviceId,
        isLocked ? 'unlock' : 'lock',
        () => {
          dispatch(
            updateStatusNotification({
              id: isLocked ? 'unlock' : 'lock',
              text: `The device is ${isLocked ? 'unlocked' : 'locked'}`,
              status: Status.SUCCESS,
              icon: isLocked ? IconLoader.icon_unlocked_status : IconLoader.icon_locked_status
            })
          );
          handleUpdateSingleDevice(device?.deviceId);
          setTimeout(() => {
            dispatch(deleteStatusNotification({ id: isLocked ? 'unlock' : 'lock' }));
          }, 3000);
        },
        (error) => {
          dispatch(
            updateStatusNotification({
              id: isLocked ? 'unlock' : 'lock',
              text: error?.message ? error.message : isString(error) ? error : '',
              status: Status.FAILURE
            })
          );
        }
      )
    );
  };

  const handleMBarSelection = (id) => {
    const donorId = props.targetDevice?.deviceId;
    const serviceIds = props.serviceDevices?.map((x) => x.deviceId) || [];
    const allDeviceIds = [donorId, ...serviceIds];

    // eslint-disable-next-line default-case
    switch (id) {
      case 'lock':
        if (!isLockUnlockActionPending) {
          isDeviceLocked(props.targetDevice)
            ? handleLockUnlockAction(props.targetDevice)
            : setIsLockUnlockModalOpen(true);
        }
        break;
      case 'blink':
        // Check if the device is online and meets the minimum API version requirement
        const apiVersion =
          props.targetDevice?.properties?.reported?.['ga2-ibms-api-version'] ||
          props.targetDevice?.properties?.reported?.ibmsApiVersion;
        if (!isDeviceOnline(props.targetDevice) || !meetMinApiVersion(apiVersion, BLINK_DEVICE_LED_MIN_API_VERSION)) {
          return;
        }

        dispatch(setStatusNotification({ id: 'blink', text: 'Turning it on...', status: Status.PENDING }));
        // For GA2, we need the serial ID but rn we are not looking for serial ID we use 'Network Element'
        const serialId = props.targetDevice?.id;
        // if targetDeviceType is "service" then pick it from props.targetDevice.type
        const networkElement = props.targetDeviceType == 'donor' ? 'DU' : props.targetDevice?.type;

        dispatch(
          doBlinkLED(
            props.targetDevice?.deviceId,
            () => {
              if (props.isGa2) {
                // Start with red LED as first color
                dispatch(
                  updateStatusNotification({
                    id: 'blink',
                    text: 'LED is blinking.',
                    icon: IconLoader.led_status_red,
                    status: Status.SUCCESS
                  })
                );

                let isRed = true;
                const GA2_BLINK_DURATION = 10000; // 10 seconds
                const BLINK_INTERVAL = 250; // 250ms alternating pattern

                const blinkInterval = setInterval(() => {
                  isRed = !isRed;
                  console.log(isRed)
                  dispatch(
                    updateStatusNotification({
                      id: 'blink',
                      text: 'LED is blinking.',
                      icon: isRed ? IconLoader.led_status_red : IconLoader.led_status_green,
                      status: Status.SUCCESS
                    })
                  );
                }, BLINK_INTERVAL);

                // Automatically close notification after 10 seconds
                setTimeout(() => {
                  clearInterval(blinkInterval);
                  handleUpdateSingleDevice(props.targetDevice?.deviceId);
                  dispatch(deleteStatusNotification({ id: 'blink' }));
                }, GA2_BLINK_DURATION);
              } else {
                // Existing GA1 behavior
                dispatch(
                  updateStatusNotification({
                    id: 'blink',
                    text: 'LED is blinking.',
                    icon: IconLoader.led_status_green,
                    status: Status.SUCCESS
                  })
                );
                setTimeout(() => {
                  dispatch(
                    updateStatusNotification({
                      id: 'blink',
                      text: 'LED is turned off.',
                      icon: IconLoader.led_status_gray,
                      status: Status.SUCCESS
                    })
                  );
                  handleUpdateSingleDevice(props.targetDevice?.deviceId);
                  setTimeout(() => {
                    dispatch(deleteStatusNotification({ id: 'blink' }));
                  }, 3000);
                }, 5000);
              }
            },
            (error) => {
              dispatch(
                updateStatusNotification({
                  id: 'blink',
                  text: error?.message ? error.message : isString(error) ? error : '',
                  status: Status.FAILURE
                })
              );
            },
            props.isGa2,
            networkElement,
            serialId
          )
        );
        break;
      case 'wifiToggle':
        if (isWiFiActionPending) return;
        let wifiMode = props.targetDevice?.properties?.reported?.wifi.status === 'enabled' ? 'disabled' : 'enabled';
        dispatch(
          setStatusNotification({
            id: 'wifiMode',
            text: wifiMode === 'enabled' ? 'Enabling Wi-Fi...' : 'Disabling Wi-Fi...',
            status: Status.PENDING,
            hideCloseButton: true
          })
        );

        const setWiFiOnSuccess = () => {
          dispatch(
            updateStatusNotification({
              id: 'wifiMode',
              text: wifiMode === 'enabled' ? 'Wi-Fi Enabled' : 'Wi-Fi Disabled',
              icon: wifiMode === 'enabled' ? IconLoader.icon_wifi_enabled : IconLoader.icon_wifi_disabled,
              status: Status.SUCCESS
            })
          );
          handleUpdateSingleDevice(props.targetDevice?.deviceId);
          setTimeout(() => {
            dispatch(deleteStatusNotification({ id: 'wifiMode' }));
          }, 3000);
        };

        const setWiFiOnError = (error) => {
          dispatch(
            updateStatusNotification({
              id: 'wifiMode',
              text: error?.message ? error.message : isString(error) ? error : '',
              icon: IconLoader.warning_circle,
              status: Status.FAILURE
            })
          );
        };

        dispatch(
          doSetWifi(props.targetDevice?.deviceId, wifiMode, null, null, null, null, setWiFiOnSuccess, setWiFiOnError)
        );
        break;
      case 'wifiAccessPoints':
        launchWiFiAccessPointModal();
        break;
      case 'wifiTimers':
        launchWiFiTimersModal();
        break;
      case 'heatmapViewExport':
        canGetRaster && setIsHeatmapViewAndExportModalOpen(true);
        break;
      case 'heatmapRetrieve':
        if (!canCreateRaster) break;
        // disable heat map retrieval if device is offline on details tab
        // Added Overview check to prevent the dialog to be displayed if DU is offline
        if ((props.source === 'details' || props.source === 'overview') && !isDeviceOnline(props.targetDevice)) break;
        setIsHeatmapRetrieveModalOpen(true);
        break;
      case 'recommission':
        setIsReCommissionModalOpen(true);
        break;
      case 'pivotBlink':
        dispatch(
          setStatusNotification({
            id: 'multiBlink',
            mode: 'multi',
            actions: {
              ...(allDeviceIds?.length &&
                allDeviceIds.reduce((acc, cur) => {
                  acc[cur] = {
                    id: 'blink',
                    type: cur === donorId ? 'donor' : 'service',
                    text: 'Turning it on...',
                    status: Status.PENDING
                  };
                  return acc;
                }, {}))
            }
          })
        );

        dispatch(
          doBlinkLED(
            props.targetDevice?.deviceId,
            () => {
              dispatch(
                updateStatusNotification({
                  id: 'multiBlink',
                  actions: {
                    [props.targetDevice?.deviceId]: {
                      text: 'LED is blinking.',
                      icon: IconLoader.led_status_green,
                      status: Status.SUCCESS
                    }
                  }
                })
              );

              setTimeout(() => {
                dispatch(
                  updateStatusNotification({
                    id: 'multiBlink',
                    actions: {
                      [props.targetDevice?.deviceId]: {
                        text: 'LED is turned off.',
                        icon: IconLoader.led_status_gray,
                        status: Status.SUCCESS
                      }
                    }
                  })
                );
                handleUpdateSingleDevice(props.targetDevice?.deviceId);
              }, 5000);
            },
            (error) => {
              dispatch(
                updateStatusNotification({
                  id: 'multiBlink',
                  actions: {
                    [props.targetDevice?.deviceId]: {
                      text: 'Failed to blink',
                      errorMsg: error,
                      icon: IconLoader.warning_circle,
                      status: Status.FAILURE
                    }
                  }
                })
              );
            }
          )
        );

        serviceIds?.length &&
          serviceIds.forEach((suId) => {
            dispatch(
              doBlinkLED(
                suId,
                () => {
                  dispatch(
                    updateStatusNotification({
                      id: 'multiBlink',
                      actions: {
                        [suId]: {
                          text: 'LED is blinking.',
                          icon: IconLoader.led_status_green,
                          status: Status.SUCCESS
                        }
                      }
                    })
                  );
                  setTimeout(() => {
                    dispatch(
                      updateStatusNotification({
                        id: 'multiBlink',
                        actions: {
                          [suId]: {
                            text: 'LED is turned off.',
                            icon: IconLoader.led_status_gray,
                            status: Status.SUCCESS
                          }
                        }
                      })
                    );

                    handleUpdateSingleDevice(suId);
                  }, 5000);
                },
                (error) => {
                  dispatch(
                    updateStatusNotification({
                      id: 'multiBlink',
                      actions: {
                        [suId]: {
                          text: 'Failed to blink',
                          errorMsg: error,
                          icon: IconLoader.warning_circle,
                          status: Status.FAILURE
                        }
                      }
                    })
                  );
                }
              )
            );
          });
        break;
      case 'pivotWifi':
        let pivotWifiMode = props.targetDevice.properties.reported?.wifi.status === 'enabled' ? 'disabled' : 'enabled';
        let trimmedPivotWifiMode = pivotWifiMode.slice(0, -2);

        dispatch(
          setStatusNotification({
            id: 'pivotWifi',
            mode: 'multi',
            actions: {
              ...(allDeviceIds?.length &&
                allDeviceIds.reduce((acc, cur) => {
                  acc[cur] = {
                    id: pivotWifiMode,
                    type: cur === donorId ? 'donor' : 'service',
                    text: trimmedPivotWifiMode + 'ing...',
                    icon: pivotWifiMode === 'enabled' ? IconLoader.blue_spinner : IconLoader.red_spinner,
                    status: Status.PENDING
                  };
                  return acc;
                }, {}))
            }
          })
        );
        dispatch(
          doSetWifi(
            props.targetDevice?.deviceId,
            pivotWifiMode,
            null,
            null,
            null,
            null,
            () => {
              dispatch(
                updateStatusNotification({
                  id: 'pivotWifi',
                  actions: {
                    [props.targetDevice?.deviceId]: {
                      text: `The Wi-Fi is ${pivotWifiMode}.`,
                      icon: pivotWifiMode === 'enabled' ? IconLoader.icon_wifi_enabled : IconLoader.icon_wifi_disabled,
                      status: Status.SUCCESS
                    }
                  }
                })
              );
              handleUpdateSingleDevice(props.targetDevice?.deviceId);
            },
            (error) => {
              dispatch(
                updateStatusNotification({
                  id: 'pivotWifi',
                  actions: {
                    [props.targetDevice?.deviceId]: {
                      text: 'Failed to ' + pivotWifiMode,
                      icon: IconLoader.warning_circle,
                      errorMsg: error,
                      status: Status.FAILURE
                    }
                  }
                })
              );
            }
          )
        );

        serviceIds?.length &&
          serviceIds.forEach((suId) => {
            dispatch(
              doSetWifi(
                suId,
                pivotWifiMode,
                null,
                null,
                null,
                null,
                () => {
                  dispatch(
                    updateStatusNotification({
                      id: 'pivotWifi',
                      actions: {
                        [suId]: {
                          text: `The Wi-Fi is ${pivotWifiMode}.`,
                          icon:
                            pivotWifiMode === 'enabled' ? IconLoader.icon_wifi_enabled : IconLoader.icon_wifi_disabled,
                          status: Status.SUCCESS
                        }
                      }
                    })
                  );
                  handleUpdateSingleDevice(suId);
                },
                (error) => {
                  dispatch(
                    updateStatusNotification({
                      id: 'pivotWifi',
                      actions: {
                        [suId]: {
                          text: 'Failed to ' + pivotWifiMode,
                          icon: IconLoader.warning_circle,
                          errorMsg: error,
                          status: Status.FAILURE
                        }
                      }
                    })
                  );
                }
              )
            );
          });
        break;
      case 'pivotLock':
        setIsPivotLockModalOpen(true);
        break;
      case 'diagRaster':
        setIsHeatmapDiagRasterModalOpen(true);
        break;
      case 'provision':
        setIsProvisionModalOpen(true);
        break;
      case 'commission':
        if (props.isGa2 && props.targetDevice?.deviceId && deviceProductType)
          dispatch(doGetArfcnValues(props.targetDevice.deviceId, deviceProductType));
        setIsCommissionModalOpen(true);
        break;
      case 'pivotSwVersion':
        setIsSwVersionModalOpen(true);
        break;
      case 'pivotRFValues':
        setIsRFValuesModalOpen(true);
        break;
    }
  };

  const disableTileAndMessage = (tiltName) => {
    let disable = true;
    let message = PERMISSION_ERROR_MESSAGE;

    switch (tiltName) {
      case 'heatmap':
        if (hasNoRasterPermissions) {
          return { disable, message };
        }
        if (props.targetDeviceType === 'service' && !isHBFServiceDevice) {
          return { disable, message: 'Device must be a HBF Service unit.' };
        }
        return { disable: false, message: 'Heatmap' };
      case 'diagRaster':
        if (!canCreateRaster) {
          return { disable, message };
        }
        if (props.isGa2) {
          return { disable, message: 'Diagnostic Raster' };
        }
        if ((props.source === 'details' || props.source === 'overview') && !isDeviceOnline(props.targetDevice)) {
          return { disable, message: DEVICE_NOT_ONLINE_ERROR_MESSAGE };
        }
        if (props.targetDeviceType === 'service' && !isHBFServiceDevice) {
          return { disable, message: 'Device must be a HBF Service unit.' };
        }
        return { disable: false, message: 'Diagnostic Raster' };
      case 'recommission':
        if (!canCommission) {
          return { disable, message };
        } else if (!isOnline) {
          return { disable, message: 'Device(s) must be online to be recommissioned.' };
        }
        return { disable: false, message: 'Re-Commission' };
      case 'commission':
        if (!canCommissionGA2(permissions)) {
          return { disable: true, message };
        }
        const validationResult = validateCommissionGA2Device(props.targetDevice);

        if (!validationResult.valid) {
          return { disable: true, message: validationResult.message };
        }

        if (criticalTargetDeviceAlarms.length > 0) {
          return { disable: true, message: 'Pivot device has critical severity alarm' };
        }

        return { disable: false };
      case 'pivotLock':
        if (props.isGa2 || !isDeviceOnline(props.targetDevice)) {
          return { disable: true, message: 'Device(s) must be online.' };
        }
        return { disable: false, message: 'Lock/Unlock' };
      case 'lock':
        if (!isOnline) {
          return { disable: true, message: DEVICE_NOT_ONLINE_ERROR_MESSAGE };
        }
        return { disable: false, message: 'Lock/Unlock' };
      case 'reboot':
        if (!isOnline) {
          return { disable: true, message: 'Device(s) must be online.' };
        }
        if (!canReboot) {
          return { disable: true, message: PERMISSION_ERROR_MESSAGE };
        }
        return { disable: false, message: 'Reboot' };
      case 'gainSettings':
        if (!isDeviceOnline(props.targetDevice)) {
          return { disable: true, message: 'Device(s) must be online.' };
        }
        if (!canSetDeviceGains) {
          return { disable: true, message: PERMISSION_ERROR_MESSAGE };
        }
        return { disable: false, message: 'Advanced Settings' };
      case 'provision':
        if (!props.isGa2) {
          return { disable: true, message: 'Device must be a GA2.' };
        }
        if (!isDeviceOnline(props.targetDevice)) {
          return { disable: true, message: 'Device(s) must be online.' };
        }
        if (!canProvisionGa2) {
          return { disable: true, message: PERMISSION_ERROR_MESSAGE };
        }
        return { disable: false, message: 'Provision' };
      default:
        return { disable, message };
    }
  };

  const heatmapOptions = ['View and Export', 'Retrieve'];
  const heatmapDisableStatus = disableTileAndMessage('heatmap');
  const heatmapDropDownTile = (
    <div
      id="maintenanceBarHeatmapTile"
      className={classNames({
        maintenanceBarHeatmapTile: true,
        maintenanceBarTile_Disabled: heatmapDisableStatus.disable,
        maintenanceBarTile_DisabledGet: canCreateRaster && !canGetRaster,
        maintenanceBarTile_DisabledCreate: !canCreateRaster && canGetRaster
      })}
      data-tip={heatmapDisableStatus.message}
    >
      <Dropdown
        role="menuitem"
        hideArrow={true}
        disabled={heatmapDisableStatus.disable}
        sendId={(selection) => handleMBarSelection(selection)}
        title={'Heatmap'}
        data={heatmapOptions.map((option) => {
          let hasPermission = true;
          // only disable offline action for individual device
          // we are disable the retrieve for the overview as well - when DU is offline
          let isOnline = isDeviceOnline(props.targetDevice);
          if ((option === 'View and Export' && !canGetRaster) || (option === 'Retrieve' && !canCreateRaster)) {
            hasPermission = false;
          }

          const isActionDisabled = !hasPermission || (option === 'Retrieve' && !isOnline);

          return (
            <div
              id="maintenanceBarHeatmapTile_Option"
              className={classNames({
                maintenanceBarHeatmapTile_Option: true,
                maintenanceBarHeatmapTile_Option_Disabled: isActionDisabled
              })}
              data-tip={hasPermission ? option : PERMISSION_ERROR_MESSAGE}
            >
              {option}
            </div>
          );
        })}
        ids={['heatmapViewExport', 'heatmapRetrieve']}
      />
    </div>
  );

  const diagRasterDisableStatus = disableTileAndMessage('diagRaster');
  const diagRasterBtnTile = (
    <div
      id="maintenanceBarHeatmapTile"
      className={classNames({
        maintenanceBarHeatmapTile: true,
        maintenanceBarTile_Disabled: props.isGa2 || diagRasterDisableStatus.disable
      })}
      data-tip={diagRasterDisableStatus.message}
    >
      <IconAndText
        role="menuitem"
        aria-disabled={diagRasterDisableStatus.disable}
        img={!diagRasterDisableStatus.disable ? IconLoader.icon_diag_raster : IconLoader.icon_diag_raster_disabled}
        hoverImg={
          !diagRasterDisableStatus.disable ? IconLoader.icon_diag_raster_hovered : IconLoader.icon_diag_raster_disabled
        }
        text={'Diag Raster'}
        onClick={() => {
          !diagRasterDisableStatus.disable && setIsHeatmapDiagRasterModalOpen(true);
        }}
      />
    </div>
  );

  const recommissionDisableStatus = disableTileAndMessage('recommission');
  const reCommissionBtnTile = (
    <MaintenancePanelTilt
      id={'maintenanceBarRecommissionTile'}
      disabled={props.isGa2 || recommissionDisableStatus.disable}
      selectionId={'recommission'}
      title={'Re-Commission'}
      hoverMessage={recommissionDisableStatus.message}
      handleMBarSelection={handleMBarSelection}
    />
  );
  const commissionGA2DisableStatus = disableTileAndMessage('commission');

  if (props.isGa2 && props.targetDeviceType === 'service' && props.source === 'details') {
    const meetsVersion = meetMinApiVersion(props.ga2IBMSVersion, BLINK_DEVICE_LED_MIN_API_VERSION);
    return (
      <GA2ServiceMenubar {...props} handleUpdate={handleUpdateSingleDevice} isGa2APIVersion={meetsVersion} />
    );
  }

  return (
    <div id="maintenanceBar" role="menubar" aria-labelledby="maintenanceBarTitleLabel" className="maintenanceBar">
      {isWifiAccessPointModalOpen && (
        <WiFiAccessPointModal
          close={() => {
            setIsWifiAccessPointModalOpen(false);
            setAccessModalStatus(Status.NONE);
          }}
          setAccessPointNameValue={setAccessPointNameValue}
          accessPointNameValue={accessPointNameValue}
          setAccessModalStatus={setAccessModalStatus}
          accessPointPasswordValue={accessPointPasswordValue}
          setAccessPointPasswordValue={setAccessPointPasswordValue}
          setIsWifiAccessPointModalOpen={setIsWifiAccessPointModalOpen}
          wifiEnabledTimerValue={wifiEnabledTimerValue}
          applyWifiAccessPoint={applyWifiAccessPoint}
          accessModalStatus={accessModalStatus}
          accessModalErrorMessage={accessModalErrorMessage}
          setWifiDisabledTimerValue={setWifiDisabledTimerValue}
        />
      )}
      {isWifiTimersModalOpen && (
        <WiFiTimersModal
          close={() => setIsWifiTimersModalOpen(false)}
          setWifiEnabledTimerValue={setWifiEnabledTimerValue}
          wifiEnabledTimerValue={wifiEnabledTimerValue}
          setWifiDisabledTimerValue={setWifiDisabledTimerValue}
          wifiDisabledTimerValue={wifiDisabledTimerValue}
          timersModalStatus={timersModalStatus}
          setIsWifiTimersModalOpen={setIsWifiTimersModalOpen}
          setAccessPointNameValue={setAccessPointNameValue}
          setAccessPointPasswordValue={setAccessPointPasswordValue}
          applyWifiTimers={applyWifiTimers}
          setTimersModalStatus={setTimersModalStatus}
        />
      )}
      {isLockUnlockModalOpen && (
        <LockUnlockModal
          device={props.targetDevice}
          close={() => setIsLockUnlockModalOpen(false)}
          action={handleLockUnlockAction}
          isActionPending={isLockUnlockActionPending}
        />
      )}
      {isPivotLockModalOpen && (
        <PivotsLockModal
          setIsPivotLockModalOpen={setIsPivotLockModalOpen}
          handlePivotLock={handlePivotLock}
          adminState={props.targetDevice.properties.reported.adminState}
          targetDevice={props.targetDevice}
          serviceDevices={props.serviceDevices}
          targetDeviceType={props.targetDeviceType}
        />
      )}
      {isHeatmapDiagRasterModalOpen && (
        <HeatmapDiagRasterModal
          donorDevice={props.targetDevice?.deviceId}
          fetchRasterRecordsForDevice={fetchRasterRecordsForDevice}
          setIsHeatmapViewAndExportModalOpen={setIsHeatmapViewAndExportModalOpen}
          onClose={() => {
            closeHeatmapDiagRasterModal();
          }}
        />
      )}
      {isHeatmapViewAndExportModalOpen && (
        <HeatmapViewAndExportModal
          donorDevice={props.targetDevice?.deviceId}
          isGa2={props.isGa2}
          onClose={() => {
            closeHeatmapViewAndExportModal();
          }}
          shouldFetchHeatmapRecords={shouldFetchHeatmapRecords}
          setShouldFetchHeatmapRecords={setShouldFetchHeatmapRecords}
          isHeatmapRetrieveModalOpen={isHeatmapRetrieveModalOpen}
          setIsHeatmapRetrieveModalOpen={setIsHeatmapRetrieveModalOpen}
          fetchRasterRecordsForDevice={fetchRasterRecordsForDevice}
          setIsCollectingFiles={setIsCollectingFiles}
        />
      )}
      {isHeatmapRetrieveModalOpen && (
        <HeatmapRetrieveModal
          targetDevice={props.targetDevice?.deviceId}
          fetchRasterRecordsForDevice={fetchRasterRecordsForDevice}
          setIsHeatmapViewAndExportModalOpen={setIsHeatmapViewAndExportModalOpen}
          onClose={() => {
            closeHeatmapRetrieveModal();
          }}
        />
      )}
      {isReCommissionModalOpen && (
        <CreateSiteModal
          fromPivotOverView={true}
          targetDonor={props.targetDevice}
          targetServices={props.serviceDevices}
          targetBundle={props.bundle}
          actionsPageThis={this}
          onClose={() => {
            closeReCommissionModal();
          }}
        />
      )}
      {isProvisionModalOpen && (
        <ProvisionModal targetDevice={props.targetDevice} onClose={() => setIsProvisionModalOpen(false)} />
      )}
      {isCommissionModalOpen && (
        <CommissionGA2Modal deviceId={props.targetDevice?.deviceId} onClose={() => setIsCommissionModalOpen(false)} />
      )}
      {isSwVersionModalOpen && <GA2SwVersionModal onClose={closeSwVersionModal} device={props.targetDevice} />}
      {isRFValuesModalOpen && (
        <GA2RFValuesModal
          device={props.targetDevice}
          handleUpdate={handleUpdateSingleDevice}
          onClose={closeRFValuesModal}
        />
      )}
      {isCollectingFiles && (
        <HeatmapImageCollectingScreen
          onClose={() => {
            setIsCollectingFiles(false);
          }}
        />
      )}

      <MaintenancePanelHeader targetDeviceType={props.targetDeviceType} source={props.source} />
      {/* Commission, Diag Raster, Heatmap, Blink LED, Lock/Unlock */}
      {props.source === 'overview' && (
        <div id="maintenanceBarRow" className="maintenanceBarRow">
          {props.targetDeviceType !== 'echo' && diagRasterBtnTile}
          {props.targetDeviceType !== 'echo' && heatmapDropDownTile}
          {props.bundle && reCommissionBtnTile}
          {props.isGa2 ? (
            <Fragment>
              <MaintenancePanelTilt
                id={'maintenanceBarRebootTile'}
                selectionId={'pivotReboot'}
                title={'Reboot'}
                disabled={disableTileAndMessage('reboot').disable}
                hoverMessage={disableTileAndMessage('reboot').message}
                handleMBarSelection={rebootDevice.trigger.onClick}
              />
              <RebootGA2DeviceDialog {...rebootDevice.dialog} />
            </Fragment>
          ) : (
            <Fragment>
              <MaintenancePanelTilt
                id={'maintenanceBarRebootTile'}
                selectionId={'pivotReboot'}
                title={'Reboot'}
                disabled={disableTileAndMessage('reboot').disable}
                hoverMessage={disableTileAndMessage('reboot').message}
                handleMBarSelection={rebootDeviceBundle.trigger.onClick}
              />
              <RebootGA1DeviceBundleDialog {...rebootDeviceBundle.dialog} />
            </Fragment>
          )}
          {/* Commented the code for hiding the BLINK LED from maintainence bar in PIVOT OVERVIEW */}
          {!props.isGa2 && (
            <MaintenancePanelTilt
              id={'maintenanceBarBlinkTile'}
              selectionId={'pivotBlink'}
              disabled={props.isGa2}
              title={'Blink LED'}
              handleMBarSelection={handleMBarSelection}
            />
          )}
          {!props.isGa2 && (
            <MaintenancePanelTilt
              id={'maintenanceBarLockTile'}
              selectionId={'pivotLock'}
              disabled={disableTileAndMessage('pivotLock').disable}
              hoverMessage={disableTileAndMessage('pivotLock').message}
              title={'Lock/Unlock'}
              handleMBarSelection={handleMBarSelection}
            />
          )}
          {props.isGa2 && (
            <MaintenancePanelTilt
              id={'maintenanceBarSwVersionTile'}
              selectionId={'pivotSwVersion'}
              disabled={!props.isGa2} // GA2-only version display
              title={'SW Versions'}
              handleMBarSelection={handleMBarSelection}
            />
          )}
          {props.isGa2 && (
            <MaintenancePanelTilt
              id={'maintenanceBarRFValuesTitle'}
              selectionId={'pivotRFValues'}
              disabled={!props.isGa2}
              title={'RF Values'}
              handleMBarSelection={handleMBarSelection}
            />
          )}
          {props.isGa2 && (
            <Fragment>
              <MaintenancePanelTilt
                id={'maintenanceBarGainSettings'}
                selectionId={'gainSettings'}
                title={'Advanced Settings'}
                disabled={disableTileAndMessage('gainSettings').disable}
                hoverMessage={disableTileAndMessage('gainSettings').message}
                handleMBarSelection={gainSettings.trigger.onClick}
              />
              <GainSettingsDialog {...gainSettings.dialog} />
            </Fragment>
          )}
        </div>
      )}
      {props.source === 'details' && (
        <div id="maintenanceBarRow" className="maintenanceBarRow">
          {['donor', 'service', 'echo'].includes(props.targetDeviceType) && diagRasterBtnTile}
          {['donor', 'service', 'echo'].includes(props.targetDeviceType) && heatmapDropDownTile}
          {!props.isGa2 && (
            <Fragment>
              <MaintenancePanelTilt
                id={'maintenanceBarRebootTile'}
                disabled={disableTileAndMessage('reboot').disable}
                hoverMessage={disableTileAndMessage('reboot').message}
                selectionId={'reboot'}
                title={'Reboot'}
                handleMBarSelection={rebootDevice.trigger.onClick}
              />
              <RebootGA1DeviceDialog {...rebootDevice.dialog} />
            </Fragment>
          )}
          {props.isGa2 ? (
            <MaintenancePanelTilt
              id={'maintenanceBarBlinkTile'}
              disabled={(() => {
                const apiVersion = props.targetDevice?.properties?.reported?.['ga2-ibms-api-version'];
                const isOnline = isDeviceOnline(props.targetDevice);
                const meetsVersion = meetMinApiVersion(apiVersion, BLINK_DEVICE_LED_MIN_API_VERSION);
                return !isOnline || !meetsVersion;
              })()}
              selectionId={'blink'}
              title={'Blink LED'}
              handleMBarSelection={handleMBarSelection}
            />
          ) : (
            <MaintenancePanelTilt
              id={'maintenanceBarBlinkTile'}
              disabled={props.isGa2 || !isDeviceOnline(props.targetDevice)}
              selectionId={'blink'}
              title={'Blink LED'}
              handleMBarSelection={handleMBarSelection}
            />
          )}

          {!props.isGa2 && (
            <MaintenancePanelTilt
              id={'maintenanceBarLockTile'}
              disabled={disableTileAndMessage('lock').disable}
              selectionId={'lock'}
              title={'Lock/Unlock'}
              hoverMessage={disableTileAndMessage('lock').message}
              handleMBarSelection={handleMBarSelection}
            />
          )}
          {(props.targetDeviceType === 'donor' || props.targetDeviceType === 'service') && !props.isGa2 && (
            <MaintenancePanelDropdownTilt
              id={'maintenanceBarWifiTile'}
              className="maintenanceBarWifiTile"
              disabled={props.isGa2 || !isDeviceOnline(props.targetDevice) || isWiFiActionPending}
              hideArrow={true}
              title={'Wi-Fi Settings'}
              hoveredOverText={isDeviceOnline(props.targetDevice) ? 'Wi-Fi Settings' : DEVICE_NOT_ONLINE_ERROR_MESSAGE}
              dropdownData={[
                `${isDeviceWiFiEnabled(props.targetDevice) ? 'Disable' : 'Enable'} Wi-Fi`,
                '',
                'Access Point',
                'Timer'
              ]}
              dropdownIds={['wifiToggle', '', 'wifiAccessPoints', 'wifiTimers']}
              handleMBarSelection={handleMBarSelection}
            />
          )}
          <MaintenanceLogsPanelTilt device={props.targetDevice} permissions={permissions} isGa2={props.isGa2} />
          <MaintenancePanelTilt
            id={'maintenanceBarProvisionTile'}
            disabled={disableTileAndMessage('provision').disable}
            selectionId={'provision'}
            title={'Provision'}
            hoverMessage={disableTileAndMessage('provision').message}
            handleMBarSelection={handleMBarSelection}
          />
          {props.isGa2 && (
            <MaintenancePanelTilt
              id={'maintenanceBarCommissionTile'}
              disabled={commissionGA2DisableStatus.disable}
              selectionId={'commission'}
              title={'Commission'}
              hoverMessage={commissionGA2DisableStatus.message}
              handleMBarSelection={handleMBarSelection}
            />
          )}
        </div>
      )}
    </div>
  );
};
